import { z } from 'zod';

import { RpeepStatus, PcfraStatus } from '../types/people';
import { nonEmptyString } from './data';

const commonPeopleFields = {
  personId: z.number(),
  propertyId: z.number(),
  householdId: z.number(),
  householdPersonId: z.number(),
  tenancyId: z.number(),
  title: nonEmptyString,
  surname: nonEmptyString,
  forename: nonEmptyString,
  initials: z.string().optional(),
  otherName: z.string().optional(),
  fullName: nonEmptyString,
  relationToTenant: nonEmptyString,
  dateOfBirth: z.iso.date(),
  preferredMethodOfContact: z.string().optional(),
  language: z.string().optional(),
  isTenant: z.boolean(),
  pcfraStatus: z.enum(PcfraStatus),
  pcfraStatusComments: z.string().optional(),
  pcfraReviewDate: z.iso.date().optional(),
  rpeepStatus: z.enum(RpeepStatus),
};

export const personSchema = z.object({
  ...commonPeopleFields,
  maritalStatus: z.string().optional(),
  gender: nonEmptyString,
  economicStatus: z.string().optional(),
  ethnicOrigin: z.string().optional(),
  sexuality: z.string().optional(),
  religion: z.string().optional(),
  genderAtBirth: z.string().optional(),
  isMainTenant: z.boolean(),
  isHistoric: z.boolean(),
  householdMemberEnded: z.string().optional(),
});

export const pcfraPersonSchema = z.object(commonPeopleFields);

export const tenantUpdateSchema = personSchema.pick({
  personId: true,
  propertyId: true,
  householdId: true,
  householdPersonId: true,
  gender: true,
  genderAtBirth: true,
  maritalStatus: true,
  economicStatus: true,
  language: true,
  ethnicOrigin: true,
  sexuality: true,
  religion: true,
  preferredMethodOfContact: true,
});

export const householdMemberUpdateSchema = tenantUpdateSchema.extend(
  personSchema.pick({
    title: true,
    surname: true,
    otherName: true,
    forename: true,
    initials: true,
    fullName: true,
    relationToTenant: true,
    dateOfBirth: true,
  }).shape,
);

export const endHouseholdMemberSchema = personSchema
  .pick({ propertyId: true, personId: true, householdId: true, householdPersonId: true })
  .extend({
    endReason: z.string().min(1),
    endDate: z.iso.date().optional(),
  });

export const camdenEmailAddressSchema = z
  .string()
  .regex(/^[^\s@]+@camden\.gov\.uk$/i, 'Please enter a valid @camden.gov.uk email address');

// For form inputs, schemas are relatively vague to satisfy existing NEC data
export const mobileNumberSchema = z.string().min(10, 'Please enter a valid UK mobile number');
export const anyPhoneNumberSchema = z.string().min(9, 'Please enter a valid UK telephone number');

export const contactDetailsFormSchema = z.object({
  emailAddresses: z.array(z.email()),
  mobileNumbers: z.array(mobileNumberSchema),
  smsOnlyNumbers: z.array(mobileNumberSchema),
  otherPhoneNumbers: z.array(anyPhoneNumberSchema),
});

export const contactTypeSchema = z.enum(['TELEPHONE', 'HOMETEL', 'MOB', 'SMSTEXTNO', 'EMAIL']);

export const contactDetailsItemSchema = z.object({
  detailsId: z.number(),
  personId: z.number(),
  type: contactTypeSchema,
  value: nonEmptyString,
});

export const personContactDetailsSchema = z.object({
  contactDetails: z.array(contactDetailsItemSchema),
});

export const personContactDetailsUpdateSchema = personContactDetailsSchema.extend({
  personId: z.number(),
  deleteContactDetailsIds: z.array(z.number()),
});

export const newPersonSchema = personSchema.extend(personContactDetailsSchema.shape);

export const createHouseholdMemberSchema = z.object({
  values: newPersonSchema,
  existingPersonInfo: z
    .object({
      personId: z.number(),
      householdPersonId: z.number().optional(),
      householdId: z.number().optional(),
      shouldEndPrevHouseholdMember: z.boolean().optional(),
      contactDetailsUpdate: personContactDetailsUpdateSchema,
    })
    .optional(),
});

export const pcfraStatusSchema = z.enum(PcfraStatus);

export const rpeepStatusSchema = z.enum(RpeepStatus);

export const updatePcfraStatusSchema = z.object({
  propertyId: z.number(),
  personId: z.number(),
  newStatus: pcfraStatusSchema,
  comments: z.string(),
  note: z.string().optional(),
  shouldNotifyReferrer: z.boolean().optional(),
});

export const updateRpeepStatusSchema = z.object({
  propertyId: z.number(),
  personId: z.number(),
  newStatus: rpeepStatusSchema,
});

export const updatePcfraReviewDateSchema = z.object({
  propertyId: z.number(),
  personId: z.number(),
  newDate: z.iso.date(),
});

export const updatePcfraSelfReferredSchema = z.object({
  propertyId: z.number(),
  personId: z.number(),
  isSelfReferred: z.boolean(),
});

export const personSearchInputSchema = z.object({
  surname: z.string().min(1, 'Required'),
  forename: z.string().min(1, 'Required'),
  dateOfBirth: z.iso.date(),
});

export const newHouseholdMemberFormValuesInputSchema = z.object({
  propertyId: z.number(),
  personId: z.number(),
});
